import { useRef, useState } from "react";
import { toast } from "sonner";
import { DownloadSimple, FilePdf, WhatsappLogo, X } from "@phosphor-icons/react";
import { elementToPdfBlob, downloadBlob, sharePdfOnWhatsapp } from "@/lib/pdf";

// Full-screen printable + shareable statement overlay.
// Optional `onExcel` callback adds an "Export Excel" button next to WhatsApp/PDF.
// `pdfFileName` names the generated PDF (both for download and for the file
// shared on WhatsApp); defaults to `${testid}.pdf` if not given.
export function PrintableStatement({
  open, onClose, shareText, onExcel, excelLabel = "Export Excel",
  testid = "statement", pdfFileName, children,
}) {
  const printRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [sharing, setSharing] = useState(false);
  if (!open) return null;

  const fileName = pdfFileName || `${testid}.pdf`;
  const text = () => (typeof shareText === "function" ? shareText() : shareText || "");

  const exportPdf = async () => {
    if (!printRef.current || exporting) return;
    try {
      setExporting(true);
      const blob = await elementToPdfBlob(printRef.current);
      downloadBlob(blob, fileName);
    } catch (e) {
      toast.error("Could not generate PDF");
    } finally {
      setExporting(false);
    }
  };

  const share = async () => {
    if (!printRef.current || sharing) return;
    try {
      setSharing(true);
      const result = await sharePdfOnWhatsapp({ el: printRef.current, fileName, text: text() });
      if (result === "fallback") {
        toast.info("PDF downloaded — attach it to the WhatsApp chat that just opened");
      } else if (result === "insecure") {
        toast.info("PDF downloaded. Direct attach needs the app opened over https — attach it from Downloads in the WhatsApp chat that just opened", { duration: 8000 });
      }
    } catch (e) {
      toast.error("Could not share PDF");
    } finally {
      setSharing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-slate-100 overflow-auto" data-testid={testid} data-printable-overlay="">
      <div className="no-print sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <button data-testid={`${testid}-close`} onClick={onClose} className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900">
          <X size={18} /> Close
        </button>
        <div className="flex items-center gap-2">
          {onExcel && (
            <button data-testid={`${testid}-excel`} onClick={onExcel}
              className="flex items-center gap-2 px-4 py-2 rounded-md bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-medium">
              <DownloadSimple size={18} weight="bold" /> {excelLabel}
            </button>
          )}
          <button data-testid={`${testid}-share`} onClick={share} disabled={sharing}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium">
            <WhatsappLogo size={18} weight="fill" /> {sharing ? "Preparing…" : "Share on WhatsApp"}
          </button>
          <button data-testid={`${testid}-pdf`} onClick={exportPdf} disabled={exporting}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white text-sm font-medium">
            <FilePdf size={18} weight="bold" /> {exporting ? "Generating…" : "Export PDF"}
          </button>
        </div>
      </div>
      <div ref={printRef} className="printable max-w-[800px] mx-auto my-6 bg-white shadow-sm border border-slate-200 rounded-lg p-8">
        {children}
      </div>
    </div>
  );
}
